import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { OptionsAssetIcon } from './OptionsAssetIcon';
import { formatEquivalentDelta, getInstrumentTypeLabel, getPortfolioRoleBadgeMeta, getPortfolioRoleLabel } from './optionsPortfolioActivityUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { formatUsd } from './optionsDashboardUtils';
import { formatSignedNative, formatSignedUsd } from './optionsPerformanceUtils';

const getSideLabel = (direction) => {
  const normalized = String(direction || '').toUpperCase();
  if (normalized === 'BUY') return 'Long';
  if (normalized === 'SELL') return 'Short';
  return '—';
};

const formatNotionalSize = (size) => {
  if (size === null || size === undefined) return '—';
  return formatUsd(size);
};

export const OptionsManagementPositionCard = ({ item, index = 0 }) => {
  const { t } = useOptionsLocale();
  const roleBadge = useMemo(() => getPortfolioRoleBadgeMeta(item?.effective_role), [item]);
  const roleLabel = useMemo(() => getPortfolioRoleLabel(item?.effective_role, t), [item, t]);
  const instrumentTypeLabel = useMemo(() => getInstrumentTypeLabel(item?.instrument_type, t), [item, t]);
  const sideLabel = getSideLabel(item?.direction);

  return (
    <div className="rounded-[28px] border border-white/10 bg-white/[0.03] px-4 py-4 sm:px-5 sm:py-5" data-testid={`options-management-position-card-${index}`}>
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2" data-testid={`options-management-position-badges-${index}`}>
            <Badge className={roleBadge.className} data-testid={`options-management-position-role-${index}`}>{roleLabel}</Badge>
            <Badge className="border-white/10 bg-white/[0.05] text-white/75" data-testid={`options-management-position-type-${index}`}>{instrumentTypeLabel}</Badge>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <OptionsAssetIcon assetKey={item?.underlying || item?.currency} size={18} />
              <p className="text-lg font-semibold text-white" data-testid={`options-management-position-title-${index}`}>{item?.instrument_name || '—'}</p>
            </div>
            <p
              className={`text-sm font-semibold ${sideLabel === 'Long' ? 'text-[#4ADE80]' : sideLabel === 'Short' ? 'text-[#F87171]' : 'text-white/58'}`}
              data-testid={`options-management-position-side-${index}`}
            >
              {sideLabel}
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[420px]" data-testid={`options-management-position-metrics-${index}`}>
          <Metric label={t('positions.futuresExposure')} value={formatEquivalentDelta(item?.delta_equivalent, item?.underlying)} testId={`options-management-position-delta-${index}`} />
          <Metric label={t('positions.notionalSize')} value={formatNotionalSize(item?.size)} testId={`options-management-position-size-${index}`} />
          <Metric
            label={t('positions.pnlLabel')}
            value={formatSignedNative(item?.floating_profit_loss, item?.currency, 4)}
            secondaryValue={item?.floating_profit_loss_usd === null || item?.floating_profit_loss_usd === undefined ? null : `≈ ${formatSignedUsd(item.floating_profit_loss_usd)}`}
            testId={`options-management-position-pnl-${index}`}
          />
        </div>
      </div>
    </div>
  );
};

const Metric = ({ label, value, secondaryValue = null, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
    <p className="mt-2 text-sm font-semibold text-white tabular-nums" data-testid={`${testId}-value`}>{value}</p>
    {secondaryValue ? <p className="mt-0.5 text-xs text-white/55 tabular-nums" data-testid={`${testId}-secondary`}>{secondaryValue}</p> : null}
  </div>
);

export default OptionsManagementPositionCard;